import { createFileRoute } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import Heading from "~/components/ui/heading";
import { FeatureSection } from "~/components/ui/feature-section";
import { Header } from "~/components/ui/header";
import { Footer } from "~/components/ui/footer";
import { CDNImage } from "~/components/ui/cdn-image";
import { createBrandMeta } from "~/lib/shared/meta";

import { IcfPartnerBox } from "~/components/icf-partner-box";
import { HeroBackground } from "~/components/ui/hero-background";

function HomePage() {
  return (
    <>
      <Header />

      <div className="relative mx-auto w-full max-w-screen-xl md:mt-4">
        <HeroBackground className="absolute inset-0 h-full max-w-full rounded-2xl" />
        <div className="relative z-10 mx-auto flex max-w-screen-sm flex-col items-center justify-center px-4 py-12 pt-24 md:py-36 md:pt-36">
          <span className="text-brand-700 mb-2 text-center font-medium">
            Welcome to <h1>Dream Coach Match</h1>
          </span>
          <h2 className="font-heading mb-4 flex flex-col items-center justify-center text-center text-4xl font-bold tracking-tight md:text-5xl lg:text-7xl">
            <span className="flex items-center gap-2 whitespace-nowrap md:h-12 lg:h-16 lg:gap-4">
              <CDNImage
                src="https://form-data.dreamcoachmatch.com/public/avatar-1.jpg"
                alt="Avatar icon"
                width={64}
                height={64}
                className="animate-breathing -animate-delay-14000 -hover:translate-y-[2px] ease-out-expo shadow-opacity-50 hover:scale-102 aspect-square rounded-full border-2 border-white shadow transition hover:rotate-3 hover:animate-none hover:shadow-lg w-10 md:w-12 lg:w-16 h-10 md:h-12 lg:h-16"
              />
              Dream Coach
            </span>
            <span className="flex items-center gap-2 lg:gap-4">
              Dream Clients
              <div className="flex items-center">
                <CDNImage
                  src="https://form-data.dreamcoachmatch.com/public/avatar-2.jpg"
                  alt="Avatar icon"
                  width={64}
                  height={64}
                  className="animate-breathing -hover:translate-y-[2px] ease-out-expo shadow-opacity-50 hover:scale-102 -hover:rotate-3 z-0 aspect-square translate-y-0 rotate-0 scale-100 rounded-full border-2 border-white shadow transition hover:z-10 hover:animate-none hover:shadow-lg w-10 md:w-12 lg:w-16 h-10 md:h-12 lg:h-16"
                />
                <CDNImage
                  src="https://form-data.dreamcoachmatch.com/public/avatar-3.jpg"
                  alt="Avatar icon"
                  width={64}
                  height={64}
                  className="animate-breathing -animate-delay-7000 -hover:translate-y-[2px] ease-out-expo z-5 shadow-opacity-50 hover:scale-102 -hover:rotate-3 -ml-4 aspect-square translate-y-0 rotate-0 scale-100 rounded-full border-2 border-white shadow transition hover:animate-none hover:shadow-lg w-10 md:w-12 lg:w-16 h-10 md:h-12 lg:h-16"
                />
              </div>
            </span>
          </h2>
          <p className="text-text-secondary mb-4 text-center sm:text-lg md:mb-8">
            At Dream Coach Match, we believe in creating connections that lead
            to delivering real, measurable results. Whether you&rsquo;re looking
            to improve your personal life or elevate your business, our platform
            connects you with the right coach to guide you toward success.
          </p>
          <div className="mt-6 flex w-full flex-col flex-nowrap items-center justify-center gap-2 sm:flex-row md:gap-4">
            <a href="/page/coach">
              <Button size="lg" variant="fancy">
                I&lsquo;m a coach
              </Button>
            </a>
            <a href="/page/member">
              <Button variant="outline" size="lg">
                I&lsquo;m looking for a coach
              </Button>
            </a>
          </div>
          <div className="shadow-brand-800/10 mt-12 rounded-xl bg-white/40 p-8 pt-4 shadow-lg backdrop-blur">
            <IcfPartnerBox />
          </div>
        </div>
      </div>

      <div className="relative z-10 w-full px-4 py-24">
        <Heading
          preHeadline="The perfect match—for you"
          headline="The platform to transform your life"
          subheadline="Professional athletes rely on coaches to achieve their results. It's our mission to spread this to every person for every area of their life."
        />

        <div className="mt-8 flex flex-col items-stretch gap-8 md:mt-16 md:gap-16">
          <FeatureSection
            preHeadline="Live now"
            headline="Beautiful, high-converting profile pages"
            description="When you register as a coach, you immediately receive your own profile page that beautifully showcases your expertise and is optimized to get you clients."
            layout="left"
            look="active"
            image={
              <CDNImage
                className="aspect-[3/2] rounded-md object-cover"
                src="https://form-data.dreamcoachmatch.com/public/screenshots/profile.png"
                alt="Examle coach profile page"
                width={600}
                height={400}
              />
            }
          >
            <a href="/page/coach">
              <Button>Apply for a coach profile</Button>
            </a>
          </FeatureSection>

          <FeatureSection
            preHeadline="Live now"
            headline="A full back-end system to acquire, manage and bill clients"
            description="Your profile will get new clients booking discovery calls with
                  you through our platform. We&rsquo;ll sync these bookings to
                  your calendar, and when the time comes you can effortlessly
                  bill them for your services."
            layout="right"
            look="active"
            image={
              <CDNImage
                className="aspect-[3/2] rounded-md object-cover"
                src="https://form-data.dreamcoachmatch.com/public/screenshots/calendar.png"
                alt="Screenshot of the calendar page in the coach app"
                width={600}
                height={400}
              />
            }
          >
            <a href="/page/coach">
              <Button>Register as a coach</Button>
            </a>
          </FeatureSection>

          <FeatureSection
            preHeadline="Coming soon"
            headline="A match made in…DreamCoachMatch"
            description="We will use your profile information, preferences, and ratings of the coaches on our platform to find the perfect match for you."
            layout="left"
            look="inactive"
            image={
              <CDNImage
                className="aspect-[3/2] rounded-md object-cover"
                src="https://form-data.dreamcoachmatch.com/public/screenshots/match.png"
                alt="The coach matching page with a score shown for each coach"
                width={600}
                height={400}
              />
            }
          ></FeatureSection>
        </div>

        <Heading
          className="mt-24"
          headline="Ready to bring your coaching practice to the next level?"
          subheadline="We&lsquo;re currently accepting a few coaches as early users."
        />
        <div className="flex w-full justify-center">
          <a href="/page/coach">
            <Button className="mx-auto">Apply for an account</Button>
          </a>
        </div>
      </div>
      <Footer />
    </>
  );
}

export const Route = createFileRoute("/")({
  head: () => {
    const metaConfig = createBrandMeta({
      title: "Dream Coach Match | The World's Coaching Marketplace",
      description:
        "Connect with certified life coaches, business coaches, and health coaches. Professional athletes rely on coaches to achieve their results. It's our mission to spread this to every person for every area of their life.",
      ogTitle: "Dream Coach Match | The World's Coaching Marketplace",
      ogDescription:
        "Connect with certified life coaches, business coaches, and health coaches. Find your perfect coach match today.",
      ogImage: "https://dreamcoachmatch.com/img/og/homepage-hero.jpg",
      ogType: "website",
      ogUrl: "https://dreamcoachmatch.com",
      twitterCard: "summary_large_image",
      twitterTitle: "Dream Coach Match | The World's Coaching Marketplace",
      twitterDescription:
        "Professional athletes rely on coaches to achieve their best results. We're on a mission to spread this to every person for every area of their life. Find your perfect coach match today.",
      twitterImage: "https://dreamcoachmatch.com/img/og/homepage-hero.jpg",
      canonical: "https://dreamcoachmatch.com",
      keywords: [
        "life coach",
        "business coach",
        "health coach",
        "executive coach",
        "coaching marketplace",
        "find a coach",
        "personal development",
        "professional coaching",
        "certified coaches",
      ],
      author: "Dream Coach Match",
    });

    return {
      ...metaConfig,
      scripts: [
        {
          key: "jsonld-website",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "@id": "https://dreamcoachmatch.com",
              name: "Dream Coach Match",
              url: "https://dreamcoachmatch.com",
              description:
                "Professional athletes rely on coaches to achieve their results. It's our mission to spread this to every person for every area of their life.",
              potentialAction: {
                "@type": "SearchAction",
                target: {
                  "@type": "EntryPoint",
                  urlTemplate:
                    "https://dreamcoachmatch.com/discovery?q={search_term_string}",
                },
                "query-input": "required name=search_term_string",
              },
            }),
          },
        },
        {
          key: "jsonld-organization",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "Dream Coach Match",
              url: "https://dreamcoachmatch.com",
              logo: "https://dreamcoachmatch.com/img/logo/dcm-horizontal-1.png",
              description:
                "The world's coaching marketplace connecting clients with certified professional coaches.",
              foundingDate: "2024",
              contactPoint: {
                "@type": "ContactPoint",
                contactType: "customer service",
                url: "https://dreamcoachmatch.com/page/contact",
              },
              areaServed: "Worldwide",
              serviceType: "Coaching Marketplace",
              sameAs: [
                "https://www.linkedin.com/company/dreamcoachmatch",
                "https://twitter.com/dreamcoachmatch",
              ],
            }),
          },
        },
        {
          key: "jsonld-service",
          props: {
            type: "application/ld+json",
            dangerouslySetInnerHTML: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              name: "Professional Coaching Marketplace",
              description:
                "Connect with certified life coaches, business coaches, health coaches, and executive coaches. Our platform helps you find the perfect coach match for your personal and professional development goals.",
              provider: {
                "@type": "Organization",
                name: "Dream Coach Match",
                url: "https://dreamcoachmatch.com",
              },
              serviceType: [
                "Life Coaching",
                "Business Coaching",
                "Health Coaching",
                "Executive Coaching",
                "Career Coaching",
              ],
              areaServed: "Worldwide",
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Coaching Services",
                itemListElement: [
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Life Coaching",
                      description:
                        "Personal development and life transformation coaching",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Business Coaching",
                      description:
                        "Business growth and leadership development coaching",
                    },
                  },
                  {
                    "@type": "Offer",
                    itemOffered: {
                      "@type": "Service",
                      name: "Health Coaching",
                      description:
                        "Wellness and health transformation coaching",
                    },
                  },
                ],
              },
            }),
          },
        },
      ],
    };
  },
  component: HomePage,
});
